import { defineStore } from 'pinia'
import { api, socket } from "@dpgradio/creative"

export const useChartStore = defineStore('chart', {
  state: () => ({
    listId: null,
    editionId: null,
    stationId: null,
    chart: null,
    searchQuery: null,
    editions: [],
    isPreview: false,
    tracksToPreview: null,
    isLoading: false,
    firstPosition: null,
    currentTrack: null,
  }),
  getters: {
    statsForTrack: (state) => ({ occurences, position}) => {
      const chart = occurences.list.find(list => list.id == state.listId);
      const edition = chart?.editions.find(edition => edition.id == state.editionId);
      return edition && {
        position,
        timesInList: edition.times_in_list,
        previousPosition: edition.previous_position,
      }
    },
    currentStart: (state) => {
      if (state.chart?.tracks?.length) {
        return state.chart.tracks[0].position
      }
      if (state.firstPosition) {
        return state.firstPosition
      }
      return 1
    },
    isSearching: (state) => !!state.searchQuery,
    hiddenTracksCount: (state) => {
      if (state.isSearching || state.isLoading) { return null }

      const { position } = (state.chart?.tracks[0] || {})
      if (!position || position > 12 || position === 1) {
        return null
      }

      return position - 1
    }
  },
  actions: {
    async setupChart({ listId, editionId, stationId, preview }) {
      this.listId = listId
      this.editionId = editionId
      this.stationId = stationId

      this.isPreview = preview !== undefined
      this.tracksToPreview = preview

      this.subscribeToUpdates()
      await this.fetchEditions()
      await this.fetchChart({ limit: this.tracksToPreview || 100})
    },
    async fetchEditions() {
      const { editions } = await this.apiRequest().get(
        `/lists/${this.listId}/editions`,
        {order: 'reverse'}
      )

      this.editions = editions

      if (!this.editionId && this.editions.length) {
        this.editionId = editions[0].id
      }
    },
    async fetchChart({ limit = 100, from = null, to = null } = {}) {
      this.isLoading = true
      try {
        const params = { limit }
        if (this.searchQuery) {
          params.q = this.searchQuery
        } else if (from != null && to != null) {
          params.from = from
          params.to = to
        }

        this.chart = await this.apiRequest().get(
          `/lists/${this.listId}/editions/${this.editionId}/tracks/played`,
          params
        )

        // Normalize track options
        this.chart.tracks = this.chart.tracks.map(({
          title,
          artist,
          thumbnail,
          position,
          selector_code: selectorCode,
          hooks,
          occurences,
          spotify_url: spotifyUrl,
          apple_music_url: appleMusicUrl,
          youtube_url: youtubeUrlFromApi,
          videos = []
        }) => {
          const youtubeVideo = videos.find(v => v.type === 'youtube')
          const youtubeUrl = youtubeUrlFromApi ?? (youtubeVideo?.id ? `https://www.youtube.com/watch?v=${youtubeVideo.id}` : undefined)

          return {
            title,
            artist,
            thumbnail,
            position,
            selectorCode,
            hooks,
            occurences,
            spotifyUrl,
            appleMusicUrl,
            youtubeUrl
          }
        })

        if (!this.firstPosition && this.chart.tracks && this.chart.tracks.length) {
          this.firstPosition = this.chart.tracks[0].position
        }
      } finally {
        this.isLoading = false
      }
    },
    subscribeToUpdates() {
      socket.join({ station: this.stationId, entity: "plays", action: "play", options: { backlog: 1 }}, (play) => {
        const activeEdition = play.active_editions.find(({ edition: { id } } ) => id == this.editionId);

        if (activeEdition) {
          this.currentTrack = {
            position: activeEdition.position,
            title: play.title,
            artist: play.artist,
            thumbnail: play.thumbnail,
            selectorCode: play.selector_code,
            hooks: play.hooks,
            stats: null // There's not enough information to say anything relevant
          }
        }
      })
    },
    search(query) {
      this.searchQuery = query
      this.fetchChart()
    },
    apiRequest() {
      let request = api.request()
      if (this.stationId) {
        request = request.withQueryParameters({ _station_id: this.stationId })
      }
      return request
    }
  },
})